import { DropResult } from 'react-beautiful-dnd';
import { create } from 'zustand';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import handleException from '@/common/utils/errors';
import { showErrorToast } from '@/common/utils/toast';

interface KanbanCard {
  id: string;
  content: string;
  column_id: string;
  order_index: number;
  created_at: string;
  description: string;
  widget_id: string;
}

export interface KanbanColumn {
  id: string;
  title: string;
  order_index: number;
  created_at: string;
  widget_id: string;
  cards: KanbanCard[];
}

interface KanbanState {
  columns: KanbanColumn[];
  loading: boolean;
  previousState: KanbanColumn[] | null;
  fetchBoard: (widgetId: string) => Promise<void>;
  addColumn: (widgetId: string, title: string) => Promise<void>;
  updateColumn: (id: string, title: string) => Promise<void>;
  deleteColumn: (id: string) => Promise<void>;
  addCard: (columnId: string, content: string) => Promise<void>;
  updateCard: (card: { id: string; content: string; description: string }) => Promise<boolean>;
  deleteCard: (id: string) => Promise<void>;
  reorderCard: (result: DropResult) => void;
  reorderColumn: (result: DropResult) => void;
  setPreviousState: (columns: KanbanColumn[]) => void;
  rollbackState: () => void;
}

export const useKanbanStore = create<KanbanState>((set, get) => ({
  columns: [],
  loading: false,
  previousState: null,

  setPreviousState: (columns) => {
    set({ previousState: columns });
  },

  rollbackState: () => {
    const { previousState } = get();
    if (previousState) {
      set({ columns: previousState, previousState: null });
    }
  },

  fetchBoard: async (widgetId: string) => {
    set({ loading: true });
    try {
      await withAuthUser(async (userId) => {
        const { data: columns, error: columnsError } = await supabase
          .from('kanban_columns')
          .select('*')
          .eq('user_id', userId)
          .eq('widget_id', widgetId)
          .order('order_index', { ascending: true });

        if (columnsError) throw columnsError;

        const { data: cards, error: cardsError } = await supabase
          .from('kanban_cards')
          .select('*')
          .eq('user_id', userId)
          .in(
            'column_id',
            columns.map((col) => col.id),
          )
          .order('order_index', { ascending: true });

        if (cardsError) throw cardsError;

        const columnsWithCards = columns.map((column) => ({
          ...column,
          cards: cards.filter((card) => card.column_id === column.id),
        }));

        set({ columns: columnsWithCards });
      });
    } catch (error) {
      handleException(error);
    } finally {
      set({ loading: false });
    }
  },

  addColumn: async (widgetId: string, title: string) => {
    const optimisticColumn: KanbanColumn = {
      id: crypto.randomUUID(),
      title,
      order_index: get().columns.length,
      created_at: new Date().toISOString(),
      widget_id: widgetId,
      cards: [],
    };

    set((state) => ({ columns: [...state.columns, optimisticColumn] }));

    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('kanban_columns')
          .insert([
            {
              title,
              user_id: userId,
              order_index: optimisticColumn.order_index,
              widget_id: widgetId,
            },
          ])
          .select()
          .single();

        if (error) throw error;
        if (data) {
          set((state) => ({
            columns: state.columns.map((col) =>
              col.id === optimisticColumn.id ? { ...data, cards: [] } : col,
            ),
          }));
        }
      });
    } catch (error) {
      set((state) => ({
        columns: state.columns.filter((col) => col.id !== optimisticColumn.id),
      }));
      handleException(error);
      showErrorToast('Failed to add column', 'The column could not be added. Please try again.');
    }
  },

  updateColumn: async (id: string, title: string) => {
    const previousColumns = get().columns;

    set((state) => ({
      columns: state.columns.map((col) => (col.id === id ? { ...col, title } : col)),
    }));

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('kanban_columns')
          .update({ title })
          .match({ id, user_id: userId });

        if (error) throw error;
      });
    } catch (error) {
      set({ columns: previousColumns });
      handleException(error);
      showErrorToast(
        'Failed to update column',
        'The column title could not be updated. Please try again.',
      );
    }
  },

  deleteColumn: async (id: string) => {
    const previousColumns = get().columns;

    set((state) => ({
      columns: state.columns.filter((col) => col.id !== id),
    }));

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('kanban_columns')
          .delete()
          .match({ id, user_id: userId });

        if (error) throw error;
      });
    } catch (error) {
      set({ columns: previousColumns });
      handleException(error);
      showErrorToast(
        'Failed to delete column',
        'The column could not be deleted. Please try again.',
      );
    }
  },

  addCard: async (columnId: string, content: string) => {
    const column = get().columns.find((col) => col.id === columnId);
    if (!column) return;

    const optimisticCard: KanbanCard = {
      id: crypto.randomUUID(),
      content,
      column_id: columnId,
      order_index: column.cards.length,
      created_at: new Date().toISOString(),
      description: '',
      widget_id: column.widget_id,
    };

    set((state) => ({
      columns: state.columns.map((col) =>
        col.id === columnId ? { ...col, cards: [...col.cards, optimisticCard] } : col,
      ),
    }));

    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('kanban_cards')
          .insert([
            {
              content,
              column_id: columnId,
              user_id: userId,
              order_index: optimisticCard.order_index,
              widget_id: column.widget_id,
            },
          ])
          .select()
          .single();

        if (error) throw error;
        if (data) {
          set((state) => ({
            columns: state.columns.map((col) => ({
              ...col,
              cards: col.cards.map((card) => (card.id === optimisticCard.id ? data : card)),
            })),
          }));
        }
      });
    } catch (error) {
      set((state) => ({
        columns: state.columns.map((col) => ({
          ...col,
          cards: col.cards.filter((card) => card.id !== optimisticCard.id),
        })),
      }));
      handleException(error);
      showErrorToast('Failed to add card', 'The card could not be added. Please try again.');
    }
  },

  updateCard: async (card: { id: string; content: string; description: string }) => {
    const previousColumns = get().columns;

    set((state) => ({
      columns: state.columns.map((col) => ({
        ...col,
        cards: col.cards.map((c) =>
          c.id === card.id ? { ...c, content: card.content, description: card.description } : c,
        ),
      })),
    }));

    try {
      const { error } = await supabase
        .from('kanban_cards')
        .update({ content: card.content, description: card.description })
        .eq('id', card.id);

      if (error) throw error;
      return true;
    } catch (error) {
      set({ columns: previousColumns });
      handleException(error);
      showErrorToast('Failed to save', 'Your changes could not be saved. Please try again.');
      return false;
    }
  },

  deleteCard: async (id: string) => {
    const previousColumns = get().columns;
    let deletedCard: KanbanCard | undefined;

    set((state) => {
      const updatedColumns = state.columns.map((col) => {
        const card = col.cards.find((c) => c.id === id);
        if (card && !deletedCard) {
          deletedCard = card;
        }
        return {
          ...col,
          cards: col.cards.filter((card) => card.id !== id),
        };
      });
      return { columns: updatedColumns };
    });

    try {
      await withAuthUser(async (userId) => {
        const { error } = await supabase
          .from('kanban_cards')
          .delete()
          .match({ id, user_id: userId });

        if (error) throw error;
      });
    } catch (error) {
      set({ columns: previousColumns });
      handleException(error);
      showErrorToast('Failed to delete card', 'The card could not be deleted. Please try again.');
    }
  },

  reorderCard: (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceColId = source.droppableId;
    const destColId = destination.droppableId;
    const sourceCol = get().columns.find((col) => col.id === sourceColId);
    const destCol = get().columns.find((col) => col.id === destColId);

    if (!sourceCol || !destCol) return;

    // Store current state for potential rollback
    get().setPreviousState([...get().columns]);

    // Create new arrays for the affected columns
    const sourceCards = [...sourceCol.cards];
    const destCards = sourceColId === destColId ? sourceCards : [...destCol.cards];

    // Remove card from source and add to destination
    const [movedCard] = sourceCards.splice(source.index, 1);
    destCards.splice(destination.index, 0, movedCard);

    // Update local state immediately
    set((state) => ({
      columns: state.columns.map((col) => {
        if (col.id === sourceColId) return { ...col, cards: sourceCards };
        if (col.id === destColId) return { ...col, cards: destCards };
        return col;
      }),
    }));

    // Update backend immediately
    withAuthUser(async (userId) => {
      try {
        const updates =
          sourceColId === destColId
            ? destCards.map((card, index) => ({
                id: card.id,
                content: card.content,
                order_index: index,
                column_id: destColId,
                user_id: userId,
                widget_id: card.widget_id,
              }))
            : [
                ...sourceCards.map((card, index) => ({
                  id: card.id,
                  content: card.content,
                  order_index: index,
                  column_id: sourceColId,
                  user_id: userId,
                  widget_id: card.widget_id,
                })),
                ...destCards.map((card, index) => ({
                  id: card.id,
                  content: card.content,
                  order_index: index,
                  column_id: destColId,
                  user_id: userId,
                  widget_id: card.widget_id,
                })),
              ];

        const { error } = await supabase.from('kanban_cards').upsert(updates);
        if (error) throw error;
        set({ previousState: null });
      } catch (error) {
        handleException(error);
        get().rollbackState();
        showErrorToast(
          'Failed to reorder card',
          'The card order could not be saved. Please try again.',
        );
      }
    });
  },

  reorderColumn: (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    // Store current state for potential rollback
    get().setPreviousState([...get().columns]);

    // Update local state immediately
    const columns = [...get().columns];
    const [movedColumn] = columns.splice(source.index, 1);
    columns.splice(destination.index, 0, movedColumn);
    set({ columns });

    // Update backend immediately
    withAuthUser(async (userId) => {
      try {
        const updates = columns.map((col, index) => ({
          id: col.id,
          title: col.title,
          order_index: index,
          user_id: userId,
          widget_id: col.widget_id,
        }));

        const { error } = await supabase.from('kanban_columns').upsert(updates);
        if (error) throw error;
        set({ previousState: null });
      } catch (error) {
        handleException(error);
        get().rollbackState();
        showErrorToast(
          'Failed to reorder column',
          'The column order could not be saved. Please try again.',
        );
      }
    });
  },
}));
